import React, { useMemo } from "react";
import { ReactComponent as IconArrowLeft } from "../assets/icon-arrow-left.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectLocation, selectPageStack } from "../store/questionnaireSlice";
import { Col, Row } from "react-bootstrap";
import { getBackDestination } from "../router";

interface Props extends React.HTMLAttributes<HTMLElement> {
  desktop?: boolean;
  className?: string;
}

function BackLink(props: Props) {
  const windowLocation = useLocation();
  const location = useSelector(selectLocation);
  const navigate = useNavigate();
  const questionnaireStack = useSelector(selectPageStack);

  // This emulates a mobile-like stack where the back button never goes 'forwards'.
  // So if the user is on the booking page, and they click one of the Edit links to
  // return them to the questionnaire, the back button will take them back to the
  // location page rather than back to the booking page (which is where the browser back
  // button would take them).
  //
  // Conceivably there's a way this could be done properly with the browser history API,
  // but RapidStor being in the mix makes things harder since AngularJS happily walks
  // all over it and ruins your ability to navigate natively.
  const destination: string | number | null | (() => void) = useMemo(() => {
    return getBackDestination(windowLocation.pathname, location, questionnaireStack);
  }, [windowLocation.pathname, location, questionnaireStack]);

  const goBack = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (typeof destination === "number") {
      navigate(destination as number);
    } else if (typeof destination === "function") {
      destination();
    } else {
      // Despite the fact that navigate() can accept both a string and a number, TypeScript
      // complains about it when written as one call since the string and number calls
      // before to different overloads. Thanks, TypeScript.
      navigate(destination as string);
    }
  };

  const linkProps = {
    className: `hidden-link opacity-50 text-dark mb-2 ${props?.className || ""} ${
      destination ? "" : "invisible"
    }`,
  };

  const element = (
    <a href={"#"} {...linkProps} style={{ lineHeight: 1 }} onClick={goBack}>
      <IconArrowLeft className={"me-2"} aria-hidden /> Back
    </a>
  );

  return props.desktop ? (
    <Row className={"d-none d-lg-flex pt-4"}>
      <Col xs={12}>{element}</Col>
    </Row>
  ) : (
    element
  );
}

export default BackLink;
